/**
 * The 401 channel. **One place the whole client learns its session is gone.**
 *
 * ┌─ ⛔ WHY A 401 IS NOT A QUERY ERROR, WHICH IS WHERE IT WOULD OTHERWISE GO ───┐
 * │ The obvious home is the query store's per-entry `error`: a fetch failed, so │
 * │ the view that asked shows something. **That is wrong about what a 401 IS.** │
 * │                                                                            │
 * │ A 500 on the conversation list is a fact about the LIST. A 401 on the       │
 * │ conversation list is a fact about the SESSION, and every other query in the │
 * │ client is about to fail the same way. Routed per query, each hook has to    │
 * │ recognise it, and each new hook can forget to - so the app would show three │
 * │ broken panels instead of one honest "your session ended".                   │
 * │                                                                            │
 * │ ⚠️ AND FORGETTING IS SILENT. A hook that treats 401 as a generic failure    │
 * │ renders its own error state and the session never gets reported, which is   │
 * │ the same shape as a component calling `fetch` directly: correct-looking,    │
 * │ and it removes the one signal that mattered.                                │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ── ⛔ THIS MODULE REPORTS. IT DOES NOT DECIDE. ─────────────────────────────
 *
 * "A request was refused" and "the session expired" are **different claims**,
 * and only the app knows which. A 401 on a cold load is somebody who was never
 * signed in; the identical 401 arriving while a user is on screen is an
 * expiry. `App.tsx` holds the user, so `App.tsx` makes that call.
 *
 * ⚠️ THAT DISTINCTION IS THE ONE `docs/INBOX-SCOPE-AND-EXIT.md` RECORDED AS
 * CONSEQUENCE 2: *"Expired and never-signed-in render the identical screen, so
 * a user logged out after ten days cannot tell that from having just arrived."*
 * It is the not-found-versus-cannot-look collapse sitting in the UI, and it is
 * closed by having the two facts in different places rather than by a better
 * message.
 */

type Listener = () => void

const listeners = new Set<Listener>()

/**
 * How many 401s have been reported. **Exposed for tests**, and for the same
 * reason `refetchPasses()` is: a client that believes it reported and did not is
 * exactly the failure this exists to prevent, and a counter is the only thing
 * that separates "reported and nobody was listening" from "never reported".
 */
let reports = 0
export function unauthorizedReports(): number {
  return reports
}

/**
 * Report that a request came back 401.
 *
 * ⚠️ CALLED FROM `data/`, ON EVERY INBOX REQUEST, THROUGH ONE WRAPPER. A fetch
 * that skips the wrapper skips this, which is the `data/` residual named in
 * `inbox-api.ts` arriving on a second axis.
 */
export function reportUnauthorized(): void {
  reports += 1
  for (const l of listeners) l()
}

/**
 * Subscribe. Returns an unsubscribe, like `subscribe()` and `onRouteChange()` -
 * a listener nobody releases outlives the component that added it.
 */
export function onUnauthorized(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Drop every listener. Test isolation only. */
export function clearUnauthorizedListeners(): void {
  listeners.clear()
}
